/**
 * Stone card web component for Shopify stone products (moissanite, gemstone, diamond).
 *
 * Standalone ES module loaded directly from assets/ (not Parcel-bundled). Reads stone
 * metafields exposed via templates/product.json.liquid and renders into the
 * <template data-type="stone-card"> element from snippets/stone-card-template.liquid.
 *
 * Depends on the globals initialized by src/scripts/app.js:
 *   - window.theme.product.loadProduct(handle)
 *   - window.theme.money.format(cents, withCurrency)
 */

const CARD_IMAGE_SRCSET_WIDTHS = [200, 300, 400, 500];
const FALLBACK_SRC_WIDTH = CARD_IMAGE_SRCSET_WIDTHS[0];
const CARD_IMAGE_SIZES = '(max-width: 767px) 400px, (min-width: 768px) 500px';

class StoneCard extends HTMLElement {

    constructor() {
        super();
    }

    connectedCallback() {

        if (!this.dataset.handle) {
            console.error('Product handle is required for stone-card');
            return;
        }

        if (this.dataset.lcpPriority === 'true') {
            this.init();
            this.hasRendered = true;
        } else {
            this.setupIntersectionObserver();
        }
    }

    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.hasRendered) {
                    this.init();
                    this.hasRendered = true;
                    this.intersectionObserver.disconnect();
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '50px'
        });

        this.intersectionObserver.observe(this);
    }

    waitForProductApi() {
        return new Promise((resolve) => {
            if (window.theme?.product?.loadProduct) {
                resolve();
                return;
            }
            window.addEventListener('theme:product:loaded', () => resolve(), { once: true });
        });
    }

    init() {
        this.waitForProductApi().then(() => {
            window.theme.product.loadProduct(this.dataset.handle).then(product => {

                const metafields = product.metafields || {};
                const cut = metafields.stone_cut || '';
                const grade = metafields.stone_grade || '';
                const origin = metafields.origin || '';
                const isMoissanite = String(metafields.stone_type || '').toLowerCase() === 'moissanite';

                const summaryParts = [];
                if (isMoissanite) {
                    if (cut) summaryParts.push(`Cut: ${cut}`);
                    if (grade) summaryParts.push(`Type: ${grade}`);
                } else {
                    // Gemstones / diamonds: "Cut Grade: <grade> | Origin: <origin>"
                    const cutGrade = cut || grade;
                    if (cutGrade) summaryParts.push(`Cut Grade: ${cutGrade}`);
                    if (origin) summaryParts.push(`Origin: ${origin}`);
                }

                const variables = {
                    productTitle: product.title || '',
                    productImageHtml: this.getImageElementHtml(product),
                    productUrl: product.url,
                    productHandle: product.handle,
                    productPrice: window.theme.money.format(product.price, true),
                    productComparePrice: product.compare_at_price && product.compare_at_price > product.price
                        ? window.theme.money.format(product.compare_at_price, true)
                        : '',
                    stoneCut: cut,
                    stoneGrade: grade,
                    stoneSummary: summaryParts.join(' | '),
                };

                this.render(variables);

                setTimeout(() => {
                    const container = this.querySelector('.stone-card__container');
                    if (container) container.classList.remove('opacity-0');
                }, 200);
            }).catch(err => {
                console.error('stone-card failed to load product', this.dataset.handle, err);
            });
        });
    }

    getTemplate(type) {
        const template = document.querySelector(`template[data-type="${type}"]`);
        if (!template) {
            console.error(`Template ${type} not found`);
            return null;
        }
        return template;
    }

    render(variables = {}) {
        const template = this.getTemplate('stone-card');
        if (!template) return;

        const content = template.content.cloneNode(true);
        const firstElement = content.firstElementChild;

        Object.keys(variables).forEach(key => {
            const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
            firstElement.innerHTML = firstElement.innerHTML.replace(regex, variables[key]);
        });

        this.innerHTML = firstElement.outerHTML;
        this.dataset.ready = true;
    }

    getSizedImageUrl(imageUrl, width) {
        if (!imageUrl) return '';
        try {
            const url = new URL(imageUrl, window.location.origin);
            url.searchParams.set('width', String(width));
            return url.toString();
        } catch (e) {
            return imageUrl;
        }
    }

    buildResponsiveSrcset(imageUrl, widths) {
        if (!imageUrl) return '';
        return widths.map(w => {
            const sized = this.getSizedImageUrl(imageUrl, w);
            return sized ? `${sized} ${w}w` : '';
        }).filter(Boolean).join(', ');
    }

    getImageElementHtml(product) {
        const isLcpHero = this.dataset.lcpPriority === 'true';
        const imageUrl = product.featured_image || window.theme.productImagePlaceholder;
        const title = product.title || '';

        if (!imageUrl) {
            return `<a href="${product.url}" class="block" aria-label="View ${title}"></a>`;
        }

        const srcset = this.buildResponsiveSrcset(imageUrl, CARD_IMAGE_SRCSET_WIDTHS);
        const src = this.getSizedImageUrl(imageUrl, FALLBACK_SRC_WIDTH) || imageUrl;
        const responsiveAttrs = srcset ? `srcset="${srcset}" sizes="${CARD_IMAGE_SIZES}"` : '';
        const loadingAttr = isLcpHero
            ? 'loading="eager" fetchpriority="high" decoding="async"'
            : 'loading="lazy" fetchpriority="low" decoding="async"';

        return `<a href="${product.url}" class="block h-full min-h-0" aria-label="View ${title}">
            <img
                src="${src}"
                ${responsiveAttrs}
                alt="${title}"
                class="w-full h-full object-cover"
                ${loadingAttr}>
        </a>`;
    }
}

if (!customElements.get('stone-card')) {
    customElements.define('stone-card', StoneCard);
}
